import { useEffect, useRef, useState } from "react";
import http, { formatApiErrorDetail } from "@/api";
import { Copy, Link2, X, Mail } from "lucide-react";
import { invitationNotice } from "@/lib/registrationEmail";
import Modal from "@/components/Modal";

const NOTICE_TONES = {
  ok: "text-green-800 bg-green-50 border-green-200",
  info: "text-blue-900 bg-blue-50 border-blue-200",
  warn: "text-amber-900 bg-amber-50 border-amber-300",
};

const STATES = {
  current: { label: "activa", color: "text-[var(--lala-blue)]" },
  expired: { label: "expirada", color: "text-red-700" },
  accepted: { label: "aceptada", color: "text-green-700" },
  replaced: { label: "reemplazada", color: "text-[var(--ink-soft)]" },
  revoked: { label: "revocada", color: "text-red-700" },
};

export default function InvitationModal({ supplierId, supplierName, onClose }) {
  const [invs, setInvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [notice, setNotice] = useState(null); // { tone, text } (spec 002, RF-4)
  const [copied, setCopied] = useState(null);
  const generating = useRef(false); // blocks a second click before the first re-render

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await http.get(`/admin/suppliers/${supplierId}/invitations`);
      setInvs(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [supplierId]);

  const generate = async () => {
    if (generating.current) return;
    generating.current = true;
    setBusy(true); setErr(""); setNotice(null);
    try {
      const { data } = await http.post(`/admin/suppliers/${supplierId}/invitations`);
      setNotice(invitationNotice(data));
      await reload();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { generating.current = false; setBusy(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm("¿Revocar esta invitación?")) return;
    setNotice(null);
    try { await http.delete(`/admin/invitations/${id}`); await reload(); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const linkFor = (token) => `${window.location.origin}/aceptar-invitacion/${token}`;

  const copy = async (token, id) => {
    try { await navigator.clipboard.writeText(linkFor(token)); setCopied(id); setTimeout(() => setCopied(null), 1500); } catch {}
  };

  return (
    <Modal testId="invitation-modal" align="center" onClose={onClose} closeOnOverlay>
      <div className="sketch-border bg-[var(--cream)] p-6 max-w-2xl w-full">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="font-hand text-lg text-[var(--lala-blue)]">invitaciones</div>
            <h3 className="font-display text-2xl">{supplierName}</h3>
            <p className="text-xs text-[var(--ink-soft)] mt-1 max-w-md">
              Genera un link de invitación (válido 7 días): se envía por correo al contacto del proveedor y
              las invitaciones anteriores dejan de funcionar. Cuando lo abran, podrán crear su contraseña
              y entrar a su scorecard.
            </p>
          </div>
          <button onClick={onClose} className="text-[var(--ink-soft)] hover:text-[var(--ink)]" data-testid="invitation-modal-close">
            <X size={20} />
          </button>
        </div>

        <button onClick={generate} disabled={busy} className="btn-primary text-sm" data-testid="invitation-generate-btn">
          <Link2 size={14} /> {busy ? "Generando…" : "Generar nueva invitación"}
        </button>

        {notice && <div className={`mt-3 text-sm border rounded-lg p-3 ${NOTICE_TONES[notice.tone] || NOTICE_TONES.ok}`} data-testid="invitation-notice" data-tone={notice.tone}>{notice.text}</div>}
        {err && <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{err}</div>}

        <div className="mt-5">
          {loading ? (
            <div className="text-center py-4 text-[var(--ink-soft)]">Cargando…</div>
          ) : invs.length === 0 ? (
            <div className="text-center py-6 text-[var(--ink-soft)] text-sm italic">Aún no hay invitaciones para este proveedor.</div>
          ) : (
            <ul className="space-y-3" data-testid="invitation-list">
              {invs.map((i) => {
                const accepted = !!i.accepted_at;
                // `state` comes from the API; the fallback only covers an older backend.
                const state = i.state || (accepted ? "accepted" : new Date(i.expires_at) < new Date() ? "expired" : "current");
                const usable = state === "current";
                const { label: status, color: statusColor } = STATES[state] || STATES.current;
                return (
                  <li key={i.id} className="border border-[var(--line)] rounded-lg p-3 bg-[var(--cream-soft)]" data-testid={`invitation-row-${i.id}`}>
                    <div className="flex items-start justify-between flex-wrap gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs">
                          <span className={`font-bold uppercase tracking-wider ${statusColor}`}>{status}</span>
                          <span className="text-[var(--ink-soft)] ml-2">creada {new Date(i.created_at).toLocaleDateString("es-MX")} por {i.created_by}</span>
                        </div>
                        {i.contact_email && (
                          <div className="text-xs text-[var(--ink-soft)] mt-1 flex items-center gap-1">
                            <Mail size={12} /> {i.contact_email}
                          </div>
                        )}
                        {usable && (
                          <div className="mt-2 p-2 rounded bg-[var(--cream)] border border-[var(--line)] font-mono text-[11px] break-all">
                            {linkFor(i.token)}
                          </div>
                        )}
                        {state === "replaced" && (
                          <div className="text-xs text-[var(--ink-soft)] mt-1" data-testid={`invitation-replaced-${i.id}`}>
                            Reemplazada por una invitación más reciente{i.replaced_at ? ` el ${new Date(i.replaced_at).toLocaleString("es-MX")}` : ""}.
                          </div>
                        )}
                        {state === "revoked" && (
                          <div className="text-xs text-red-700 mt-1" data-testid={`invitation-revoked-${i.id}`}>
                            Revocada{i.revoked_by ? ` por ${i.revoked_by}` : ""}{i.revoked_at ? ` el ${new Date(i.revoked_at).toLocaleString("es-MX")}` : ""}.
                          </div>
                        )}
                        {accepted && (
                          <div className="text-xs text-green-700 mt-1">Aceptada el {new Date(i.accepted_at).toLocaleString("es-MX")}</div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {usable && (
                          <>
                            <button onClick={() => copy(i.token, i.id)} className="sketch-pill bg-[var(--cream)] px-3 py-1 text-xs font-semibold text-[var(--lala-blue)]" data-testid={`invitation-copy-${i.id}`}>
                              <Copy size={12} className="inline mr-1" /> {copied === i.id ? "¡Copiado!" : "Copiar link"}
                            </button>
                            <button onClick={() => revoke(i.id)} className="text-red-700 hover:underline text-xs" data-testid={`invitation-revoke-${i.id}`}>Revocar</button>
                          </>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </Modal>
  );
}
